import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  fetchModuleByOrder,
  fetchMyModuleTaskProgress,
  getMyEmployeeId,
  getSignedVideoUrl,
  setModuleTaskDone,
} from "./Utils";
import { useI18n } from "../../i18n/I18nProvider";
import { brand } from "../../theme";
import type { Module, ModuleTask, EnrichedLink, EnrichedModuleContent, EnrichedTaskContent, ModuleWrapperProps } from "../../types/modules";


const PDF_PATHS = {
  documents: "/pdfs/takeKONTROL_PDF01_Document_Folder.pdf",
  backpack: "/pdfs/takeKONTROL_PDF02_Backpack.pdf",
  familyComms: "/pdfs/takeKONTROL_PDF03_Family_Comms.pdf",
  medicalCard: "/pdfs/takeKONTROL_PDF04_Medical_Card.pdf",
  shelfInventory: "/pdfs/takeKONTROL_PDF05_Shelf_Inventory.pdf",
  shelterPlan: "/pdfs/takeKONTROL_PDF06_Shelter_Plan.pdf",
  mentalCard: "/pdfs/takeKONTROL_PDF07_Mental_Card.pdf",
  physicalRoutine: "/pdfs/takeKONTROL_PDF08_Physical_Routine.pdf",
};

const MODULE_ENRICHMENT: Record<number, EnrichedModuleContent> = {
  1: {
    outcome_en:
      "After this module, your household can operate independently for 7 days without electricity, water or supply chains.",
    outcome_de:
      "Nach diesem Modul kann dein Haushalt 7 Tage lang unabhängig von Strom, Wasser oder Lieferketten funktionieren.",
    tasks: {
      1: {
        objective_en: "Know exactly how much food, water and hygiene your household needs.",
        objective_de: "Genau wissen, wie viel Nahrung, Wasser und Hygiene dein Haushalt benötigt.",
        action_en: "Use an emergency-supply calculator to estimate the quantities for your household.",
        action_de: "Nutze einen Vorratskalkulator, um die Mengen für deinen Haushalt zu berechnen.",
        resources: [
          {
            label_en: "Emergency supply calculator",
            label_de: "Vorratskalkulator",
            href: "https://www.ernaehrungsvorsorge.de/private-vorsorge/notvorrat/vorratskalkulator/",
          },
        ],
      },
      2: {
        objective_en: "Know what you already have and what is missing.",
        objective_de: "Wissen, was bereits vorhanden ist und was noch fehlt.",
        action_en: "Complete the Home Emergency Shelf Inventory checklist.",
        action_de: "Fülle die Home Emergency Shelf Inventory Checkliste aus.",
        downloads: [
          {
            label_en: "Home Emergency Shelf Inventory",
            label_de: "Notfall-Vorratsinventar",
            href: PDF_PATHS.shelfInventory,
          },
        ],
      },
      3: {
        objective_en: "Have a complete shopping list for missing items.",
        objective_de: "Eine vollständige Einkaufsliste für fehlende Dinge haben.",
        action_en: "Use your inventory and fill the “Needed” column.",
        action_de: "Nutze dein Inventar und fülle die Spalte „Benötigt“ aus.",
        downloads: [
          {
            label_en: "Shelf Inventory PDF",
            label_de: "Vorratsinventar PDF",
            href: PDF_PATHS.shelfInventory,
          },
        ],
      },
      4: {
        objective_en: "Keep supplies fresh and ready.",
        objective_de: "Vorräte frisch und einsatzbereit halten.",
        action_en: "Set a calendar reminder every 6 months to check and rotate supplies.",
        action_de:
          "Setze alle 6 Monate eine Kalendererinnerung zum Prüfen und Rotieren deiner Vorräte.",
        downloads: [
          {
            label_en: "Shelf Inventory PDF",
            label_de: "Vorratsinventar PDF",
            href: PDF_PATHS.shelfInventory,
          },
        ],
      },
      5: {},
      6: {
        objective_en: "Prepare meals even during a power outage.",
        objective_de: "Auch bei Stromausfall Mahlzeiten zubereiten können.",
        action_en: "Get guidance for cooking without electricity and test-cook 1-2 simple recipes.",
        action_de:
          "Besorge dir eine Anleitung zum Kochen ohne Strom und teste 1-2 einfache Rezepte.",
        resources: [
          {
            label_en: "BBK cooking without electricity",
            label_de: "BBK Kochen ohne Strom",
            href: "https://www.bbk.bund.de/EN/Prepare-for-disasters/Personal-Preparedness/personal-preparedness_node.html",
          },
        ],
      },
      7: {},
      8: {},
      9: {
        objective_en: "Maintain basic cleanliness even without power or water.",
        objective_de: "Grundlegende Hygiene auch ohne Strom oder Wasser aufrechterhalten.",
        action_en:
          "Prepare washing-water containers, define an alternative toilet solution, and stock up on wet wipes.",
        action_de:
          "Bereite Wasserbehälter zum Waschen vor, plane eine alternative Toilettenlösung und lege Feuchttücher bereit.",
        resources: [
          {
            label_en: "Water container / solar shower",
            label_de: "Wasserbehälter / Solardusche",
            href: "https://holz4home.de/products/solardusche-garten",
          },
          {
            label_en: "Dry toilet option",
            label_de: "Trockentoiletten-Option",
            href: "https://boxio.de/products/trenntoilette",
          },
        ],
      },
      10: {
        objective_en: "Ensure you have reliable lighting during a power outage.",
        objective_de: "Sicherstellen, dass bei einem Stromausfall ausreichend Licht verfügbar ist.",
        action_en: "Purchase flashlights, LED tea lights or emergency candles and store them in easily accessible locations.",
        action_de: "Beschaffe Taschenlampen, LED-Teelichter oder Notfallkerzen und lagere sie an leicht erreichbaren Orten.",
        resources: [
          {
            label_en: "Lux Calculator",
            label_de: "Lux-Rechner",
            href: "https://www.ledzone.de/pages/lux-rechner-fur-raumbeleuchtung",
          },
        ],
      },
      11: {
        objective_en: "Verify that your emergency lighting actually works when needed.",
        objective_de: "Überprüfen, dass die Notbeleuchtung im Ernstfall tatsächlich funktioniert.",
        action_en:
          "Test all flashlights and emergency lights, replace batteries if needed and verify charging status.",
        action_de:
          "Teste alle Taschenlampen und Notleuchten, tausche Batterien bei Bedarf aus und prüfe den Ladezustand.",
      },
      12: {
        objective_en: "Maintain safe indoor temperatures during cold-weather emergencies.",
        objective_de: "Auch bei winterlichen Notfällen eine sichere Raumtemperatur aufrechterhalten.",

        action_en:
          "Prepare warm blankets, sleeping bags and thermal clothing for all household members.",
        action_de:
          "Halte warme Decken, Schlafsäcke und Thermokleidung für alle Haushaltsmitglieder bereit.",
      },
      13: {
        objective_en: "Prepare a backup heating solution for extended power outages.",
        objective_de: "Eine alternative Heizlösung für längere Stromausfälle vorbereiten.",
        action_en: "Evaluate suitable gas-heater options and ensure fuel cartridges are available and stored safely.",
        action_de: "Prüfe geeignete Gasheizer und stelle sicher, dass ausreichend Kartuschen vorhanden und sicher gelagert sind.",
        safety_en: "When using a gas heater, always ensure a carbon-monoxide (CO) detector is present and functioning.",
        safety_de: "Bei Verwendung eines Gasheizers muss ein funktionsfähiger Kohlenmonoxid-Melder (CO-Melder) vorhanden sein.",
        resources: [
          {
            label_en: "Gas Heater Options",
            label_de: "Gasheizer-Optionen",
            href: "https://www.obi.de/search/gasheizer/",
          },
        ],
      },
      14: {
        objective_en: "Reduce indoor temperatures during heat waves.",
        objective_de: "Die Raumtemperatur während Hitzewellen reduzieren.",
        action_en: "Use shutters, blinds or curtains to darken windows during periods of extreme heat.",
        action_de: "Nutze Rollläden, Jalousien oder Vorhänge, um Fenster während extremer Hitze abzudunkeln.",
        resources: [
          {
            label_en: "UBA Heat Protection Guide",
            label_de: "UBA Hitzeknigge",
            href: "https://www.umweltbundesamt.de/publikationen/hitzeknigge",
          },
          {
            label_en: "Handheld Fan Options",
            label_de: "Handventilator-Optionen",
            href: "https://www.amazon.de/handventilator/s?k=handventilator",
          },
        ],
      },
      15: {
        objective_en: "Know how to use a fire extinguisher safely before an emergency happens.",
        objective_de: "Wissen, wie du einen Feuerlöscher sicher benutzt, bevor ein Notfall eintritt.",
        action_en: "Practice the basic handling steps with a suitable training source or qualified person.",
        action_de: "Übe die grundlegende Handhabung mit einer geeigneten Anleitung oder einer qualifizierten Person.",
        safety_en: "Fire-safety content should be reviewed by a qualified expert.",
        safety_de: "Brandschutz-Inhalte sollten fachlich geprüft werden.",
        resources: [
          {
            label_en: "Fire extinguishing spray",
            label_de: "Feuerlöschspray",
            href: "https://www.prymos.com/feuerloeschspray-universal",
          },
        ],
      },
      16: {
        objective_en: "Reduce fire and smoke risk by preparing simple protective equipment.",
        objective_de: "Brand- und Rauchrisiken durch einfache Schutzausrüstung reduzieren.",
        action_en: "Check whether a fire blanket or fire protection hood is useful for your household and store it accessibly.",
        action_de: "Prüfe, ob eine Löschdecke oder Fluchthaube für deinen Haushalt sinnvoll ist, und lagere sie griffbereit.",
        resources: [
          {
            label_en: "Fire escape hoods",
            label_de: "Brandfluchthauben",
            href: "https://sqs-shop.de/brandschutz/brandfluchthauben/",
          },
        ],
      },
      17: {
        objective_en: "Ensure access to independent backup power.",
        objective_de: "Unabhängige Notstromversorgung vorbereiten.",
        action_en:
          "Keep a power bank fully charged.",
        action_de:
          "Halte eine Powerbank geladen.",
      },
      18: {
        objective_en: "Ensure access to independent backup power.",
        objective_de: "Unabhängige Notstromversorgung vorbereiten.",
        action_en:
          "Optionally evaluate plug-in solar with storage or a medically approved power station.",
        action_de:
          "Optional: Balkonsolar mit Speicher oder eine medizinisch geeignete Powerstation prüfen.",
        safety_en: "Any power station used for medical devices must be approved for that purpose.",
        safety_de:
          "Powerstations für medizinische Geräte müssen für diesen Zweck zugelassen sein.",
        resources: [
          {
            label_en: "Plug-in solar guide",
            label_de: "Balkonsolar-Ratgeber",
            href: "https://www.verbraucherzentrale.de/wissen/energie/erneuerbare-energien/steckersolar-solarstrom-vom-balkon-direkt-in-die-steckdose-44715",
          },
          {
            label_en: "Power stations",
            label_de: "Powerstationen",
            href: "https://de.ecoflow.com/collections/delta-series-portable-power-stations",
          },
        ],
      },
    },
  },

  2: {
    outcome_en:
      "After this module, you can leave your home quickly with everything you need for 72 hours.",
    outcome_de:
      "Nach diesem Modul kannst du dein Zuhause schnell mit allem verlassen, was du für 72 Stunden brauchst.",
    tasks: {
      1: {
        objective_en: "Have a fully packed, ready-to-grab emergency backpack.",
        objective_de: "Einen vollständig gepackten Notfallrucksack griffbereit haben.",
        action_en: "Complete the Emergency Backpack Packing Checklist.",
        action_de: "Fülle die Notfallrucksack-Checkliste aus.",
        downloads: [
          {
            label_en: "Emergency Backpack Checklist",
            label_de: "Notfallrucksack-Checkliste",
            href: PDF_PATHS.backpack,
          },
        ],
        resources: [
          {
            label_en: "BBK emergency pack guide",
            label_de: "BBK Notgepäck-Ratgeber",
            href: "https://www.bbk.bund.de/EN/Prepare-for-disasters/Personal-Preparedness/Emergency-pack/emergency-pack_node.html",
          },
        ],
      },
      2: {
        objective_en: "Know the maximum backpack weight you can carry for one hour.",
        objective_de: "Wissen, welches Rucksackgewicht du eine Stunde tragen kannst.",
        action_en: "Walk for one hour with your packed backpack.",
        action_de: "Gehe eine Stunde mit deinem gepackten Rucksack.",
        note_en: "Do not overload the bag. A backpack keeps both hands free.",
        note_de: "Überlade den Rucksack nicht. Ein Rucksack hält beide Hände frei.",
      },
      3: {
        objective_en: "Know whether you can be ready to leave within your target time.",
        objective_de: "Wissen, ob du innerhalb deiner Zielzeit los kannst.",
        action_en: "Test evacuation from “alarm” to “out the door.”",
        action_de: "Teste die Evakuierung von „Alarm“ bis „aus der Tür“.",
        note_en:
          "Target time depends on the scenario. Under 60 seconds is mainly relevant for fire or air-raid type situations.",
        note_de:
          "Die Zielzeit hängt vom Szenario ab. Unter 60 Sekunden ist vor allem bei Brand- oder Luftalarm-Situationen relevant.",
      },
      4: {
        objective_en: "Have a plan for annual review.",
        objective_de: "Einen Plan für die jährliche Prüfung haben.",
        action_en: "Set a calendar reminder every year to check and refresh your backpack.",
        action_de:
          "Setze jedes Jahr eine Kalendererinnerung, um den Rucksack zu prüfen und aufzufrischen.",
        downloads: [
          {
            label_en: "Emergency Backpack Checklist",
            label_de: "Notfallrucksack-Checkliste",
            href: PDF_PATHS.backpack,
          },
        ],
      },
      5: {
        objective_en: "Know what must go into a backpack for children.",
        objective_de: "Wissen, was in einen Notfallrucksack für Kinder gehört.",
        action_en: "Pack a child's emergency backpack, adjusted to age and weight.",
        action_de: "Packe einen Kinder-Notfallrucksack, angepasst an Alter und Gewicht.",
        resources: [
          {
            label_en: "DRK children's emergency backpack",
            label_de: "DRK Kinder-Notfallrucksack",
            href: "https://www.drk.de/hilfe-in-deutschland/bevoelkerungsschutz-und-rettung/",
          },
        ],
      },
    },
  },

  3: {
    outcome_en:
      "After this module, you can provide first aid and emergency care until professional help arrives.",
    outcome_de:
      "Nach diesem Modul kannst du Erste Hilfe und Notfallversorgung leisten, bis professionelle Hilfe eintrifft.",
    tasks: {
      1: {
        objective_en: "Provide medical first aid until the ambulance arrives.",
        objective_de: "Medizinische Erste Hilfe leisten, bis der Rettungsdienst eintrifft.",
        action_en: "Complete a basic first-aid course.",
        action_de: "Absolviere einen Erste-Hilfe-Grundkurs.",
        resources: [
          {
            label_en: "DRK first-aid courses",
            label_de: "DRK Erste-Hilfe-Kurse",
            href: "https://www.drk.de/en/services-germany/red-cross-first-aid-course/",
          },
          {
            label_en: "Johanniter first-aid courses",
            label_de: "Johanniter Erste-Hilfe-Kurse",
            href: "https://www.johanniter.de/kurse/",
          },
          {
            label_en: "Malteser first-aid courses",
            label_de: "Malteser Erste-Hilfe-Kurse",
            href: "https://www.malteser.de/kurse/erste-hilfe.html",
          },
        ],
      },
      2: {
        objective_en: "Have a first-aid kit ready at home.",
        objective_de: "Eine vollständige Hausapotheke und Erste-Hilfe-Ausstattung bereithalten.",
        action_en: "Check your first-aid kit against the Home Emergency Shelf Inventory.",
        action_de: "Prüfe dein Erste-Hilfe-Set mit dem Notfall-Vorratsinventar.",
        downloads: [
          {
            label_en: "Shelf Inventory PDF",
            label_de: "Vorratsinventar PDF",
            href: PDF_PATHS.shelfInventory,
          },
        ],
        resources: [
          {
            label_en: "BBK medicine cabinet guide",
            label_de: "BBK Hausapotheke",
            href: "https://www.bbk.bund.de/EN/Prepare-for-disasters/Personal-Preparedness/Medicine-cabinet/medicine-cabinet_node.html",
          },
        ],
      },
      3: {
        objective_en: "Have critical health information available for responders.",
        objective_de: "Wichtige Gesundheitsinformationen für Helfende verfügbar machen.",
        action_en: "Complete the Medical and First-Aid Information Card.",
        action_de: "Fülle die Medizin- und Erste-Hilfe-Informationskarte aus.",
        downloads: [
          {
            label_en: "Medical & First-Aid Card",
            label_de: "Medizin- & Erste-Hilfe-Karte",
            href: PDF_PATHS.medicalCard,
          },
        ],
      },
      4: {
        objective_en: "Prepare support for care-dependent household members.",
        objective_de: "Unterstützung für pflegebedürftige Haushaltsmitglieder vorbereiten.",
        action_en:
          "Create a checklist of needed aids such as evacuation sheets and approved power supply for medical devices.",
        action_de:
          "Erstelle eine Liste notwendiger Hilfsmittel wie Evakuierungshilfen und zugelassene Stromversorgung für medizinische Geräte.",
        safety_en: "Power for medical devices must come from an approved source.",
        safety_de: "Strom für medizinische Geräte muss aus einer zugelassenen Quelle kommen.",
      },
      5: {
        objective_en: "Quickly make an emergency call, even without speaking.",
        objective_de: "Schnell einen Notruf absetzen können, auch ohne zu sprechen.",
        action_en: "Install the nora emergency app.",
        action_de: "Installiere die nora Notruf-App.",
        resources: [
          {
            label_en: "nora emergency app",
            label_de: "nora Notruf-App",
            href: "https://www.nora-notruf.de/en-en",
          },
        ],
      },
      6: {
        objective_en: "Have emergency numbers ready at all times.",
        objective_de: "Notrufnummern jederzeit griffbereit haben.",
        action_en:
          "Save 112, 110, 116117, 116 123 and TelefonSeelsorge numbers in your phone and keep a printed copy.",
        action_de:
          "Speichere 112, 110, 116117, 116 123 und TelefonSeelsorge im Handy und halte eine gedruckte Kopie bereit.",
        note_en:
          "112 = fire/rescue, 110 = police, 116117 = medical on-call, 116 123 and TelefonSeelsorge = emotional support.",
        note_de:
          "112 = Feuerwehr/Rettung, 110 = Polizei, 116117 = ärztlicher Bereitschaftsdienst, 116 123 und TelefonSeelsorge = emotionale Unterstützung.",
      },
    },
  },

  4: {
    outcome_en:
      "After this module, your critical documents are secured, digitized and accessible from anywhere.",
    outcome_de:
      "Nach diesem Modul sind deine kritischen Dokumente gesichert, digitalisiert und von überall zugänglich.",
    tasks: {
      1: {
        objective_en: "Know exactly which documents must be secured.",
        objective_de: "Genau wissen, welche Dokumente gesichert werden müssen.",
        action_en: "Complete the Emergency Document Folder Checklist.",
        action_de: "Fülle die Notfall-Dokumentenmappe-Checkliste aus.",
        downloads: [
          {
            label_en: "Document Folder Checklist",
            label_de: "Dokumentenmappe-Checkliste",
            href: PDF_PATHS.documents,
          },
        ],
        resources: [
          {
            label_en: "BBK save documents guide",
            label_de: "BBK Dokumente sichern",
            href: "https://www.bbk.bund.de/EN/Prepare-for-disasters/Personal-Preparedness/Save-documents/save-documents_node.html",
          },
        ],
      },
      2: {
        objective_en: "Have secure digital copies of all important documents.",
        objective_de: "Sichere digitale Kopien aller wichtigen Dokumente haben.",
        action_en: "Digitize your documents and store copies securely and encrypted.",
        action_de: "Digitalisiere deine Dokumente und speichere Kopien sicher und verschlüsselt.",
        safety_en: "Do not store passwords or sensitive documents insecurely.",
        safety_de: "Speichere Passwörter oder sensible Dokumente niemals unsicher.",
        resources: [
          {
            label_en: "BSI security tips",
            label_de: "BSI Sicherheitstipps",
            href: "https://www.bsi.bund.de/EN/Home/home_node.html",
          },
        ],
      },
      3: {
        objective_en: "Store documents safely and accessibly.",
        objective_de: "Dokumente sicher und zugänglich aufbewahren.",
        action_en:
          "Keep originals and copies in a waterproof portable sleeve and ensure a trusted person knows where it is kept.",
        action_de:
          "Bewahre Originale und Kopien in einer wasserfesten Mappe auf und informiere eine Vertrauensperson über den Ort.",
        downloads: [
          {
            label_en: "Document Folder Checklist",
            label_de: "Dokumentenmappe-Checkliste",
            href: PDF_PATHS.documents,
          },
        ],
      },
    },
  },

  5: {
    outcome_en:
      "After this module, you can stay informed, reachable and connected even when networks fail.",
    outcome_de:
      "Nach diesem Modul bleibst du informiert, erreichbar und verbunden, auch wenn Netze ausfallen.",
    tasks: {
      1: {
        objective_en: "Ensure you can always make emergency calls.",
        objective_de: "Sicherstellen, dass du jederzeit Notrufe absetzen kannst.",
        action_en: "Set a daily reminder to check your phone battery and keep a power bank charged.",
        action_de:
          "Setze eine tägliche Erinnerung zum Akkustand und halte eine Powerbank geladen.",
      },
      2: {
        objective_en: "Stay prepared for mobile-network failures.",
        objective_de: "Auf Mobilfunkausfälle vorbereitet bleiben.",
        action_en: "Check alternative communication methods such as radio or satellite options.",
        action_de:
          "Prüfe alternative Kommunikationswege wie Radio oder Satellitenoptionen.",
      },
      3: {
        objective_en: "Communicate within your family or circle during a network outage.",
        objective_de: "Bei Netzausfall im Familien- oder Freundeskreis kommunizieren können.",
        action_en: "Install and test a mesh communication app.",
        action_de: "Installiere und teste eine Mesh-Kommunikations-App.",
        resources: [
          {
            label_en: "Bridgefy",
            label_de: "Bridgefy",
            href: "https://bridgefy.me/",
          },
          {
            label_en: "Meshtastic",
            label_de: "Meshtastic",
            href: "https://meshtastic.org/",
          },
        ],
      },
      4: {
        objective_en: "Have important contacts readily available.",
        objective_de: "Wichtige Kontakte schnell verfügbar haben.",
        action_en: "Save emergency contacts on your phone and keep a physical copy.",
        action_de: "Speichere Notfallkontakte im Handy und halte eine physische Kopie bereit.",
      },
      5: {
        objective_en: "Make contact chains clear before phones or internet become unreliable.",
        objective_de: "Kontaktketten klären, bevor Telefon oder Internet unzuverlässig werden.",
        action_en: "Complete the Family Communication Plan Template.",
        action_de: "Fülle die Vorlage für den Familien-Kommunikationsplan aus.",
        downloads: [
          {
            label_en: "Family Communication Plan",
            label_de: "Familien-Kommunikationsplan",
            href: PDF_PATHS.familyComms,
          },
        ],
      },
      6: {
        objective_en: "Ensure the right people get the right information at the right time.",
        objective_de: "Sicherstellen, dass die richtigen Personen rechtzeitig informiert werden.",
        action_en: "Define who calls whom, who checks on children, pets or dependents, and who monitors warnings.",
        action_de: "Lege fest, wer wen anruft, wer Kinder, Haustiere oder Abhängige prüft und wer Warnungen beobachtet.",
      },
      7: {
        objective_en: "Always receive relevant warning messages.",
        objective_de: "Relevante Warnmeldungen zuverlässig erhalten.",
        action_en: "Install NINA, KATWARN or local warning apps.",
        action_de: "Installiere NINA, KATWARN oder lokale Warn-Apps.",
        resources: [
          {
            label_en: "BBK Keep Up To Date / NINA",
            label_de: "BBK Warn-App NINA",
            href: "https://www.bbk.bund.de/EN/Prepare-for-disasters/Personal-Preparedness/Keep-up-to-date/keep-up-to-date_node.html",
          },
        ],
      },
      8: {
        objective_en: "Know where to get trustworthy information.",
        objective_de: "Wissen, wo verlässliche Informationen zu finden sind.",
        action_en: "Create a list of reliable sources and learn to recognize misinformation.",
        action_de:
          "Erstelle eine Liste verlässlicher Quellen und lerne, Desinformation zu erkennen.",
        resources: [
          {
            label_en: "Mimikama fact-checking",
            label_de: "Mimikama Faktencheck",
            href: "https://www.mimikama.org/",
          },
          {
            label_en: "BBK trusted information",
            label_de: "BBK vertrauenswürdige Informationen",
            href: "https://www.bbk.bund.de/EN/Home/home_node.html",
          },
        ],
      },
    },
  },

  6: {
    outcome_en:
      "After this module, you know where to shelter, how to evacuate, and your family knows the plan too.",
    outcome_de:
      "Nach diesem Modul weißt du, wo du Schutz findest, wie du evakuierst, und deine Familie kennt den Plan.",
    tasks: {
      1: {
        objective_en: "Know when to shelter, where to go and how to move safely.",
        objective_de: "Wissen, wann du Schutz suchst, wohin du gehst und wie du sicher dorthin kommst.",
        action_en: "Complete the Shelter and Evacuation Plan.",
        action_de: "Fülle den Schutz- und Evakuierungsplan aus.",
        downloads: [
          {
            label_en: "Shelter & Evacuation Plan",
            label_de: "Schutz- & Evakuierungsplan",
            href: PDF_PATHS.shelterPlan,
          },
        ],
      },
      2: {
        objective_en: "Know official shelters, meeting points and emergency accommodation in your region.",
        objective_de: "Offizielle Schutzorte, Treffpunkte und Notunterkünfte in deiner Region kennen.",
        action_en: "Contact your municipality for local shelter information.",
        action_de: "Kontaktiere deine Kommune für lokale Schutzraum-Informationen.",
        note_en: "Germany has no central public-shelter register; availability varies by region.",
        note_de:
          "In Deutschland gibt es kein zentrales öffentliches Schutzraumregister; die Verfügbarkeit ist regional unterschiedlich.",
      },
      3: {
        objective_en: "Be prepared for different types of emergencies.",
        objective_de: "Auf verschiedene Notfallszenarien vorbereitet sein.",
        action_en: "Create evacuation plans for fire, flood, power outage and gas leak scenarios.",
        action_de:
          "Erstelle Evakuierungspläne für Brand, Hochwasser, Stromausfall und Gasleck.",
        downloads: [
          {
            label_en: "Shelter & Evacuation Plan",
            label_de: "Schutz- & Evakuierungsplan",
            href: PDF_PATHS.shelterPlan,
          },
        ],
      },
      4: {
        objective_en: "Identify obstacles or weaknesses in the plan.",
        objective_de: "Hindernisse oder Schwachstellen im Plan erkennen.",
        action_en: "Conduct a trial evacuation with your household.",
        action_de: "Führe eine Probe-Evakuierung mit deinem Haushalt durch.",
      },
      5: {
        objective_en: "Family, relatives, friends and neighbors know the evacuation plan.",
        objective_de: "Familie, Angehörige, Freunde und Nachbarn kennen den Evakuierungsplan.",
        action_en: "Share your plan and set a meeting schedule.",
        action_de: "Teile deinen Plan und lege Treffzeiten fest.",
      },
    },
  },

  7: {
    outcome_en:
      "After this module, you have the strength and endurance to carry, climb and help others in a crisis.",
    outcome_de:
      "Nach diesem Modul hast du Kraft und Ausdauer, um in einer Krise zu tragen, Treppen zu steigen und anderen zu helfen.",
    tasks: {
      1: {
        objective_en: "Build useful movement capacity gradually and safely.",
        objective_de: "Nützliche Bewegungsfähigkeit langsam und sicher aufbauen.",
        action_en: "Follow the Physical Readiness Routine weekly plan.",
        action_de: "Folge dem Wochenplan der Physical Readiness Routine.",
        downloads: [
          {
            label_en: "Physical Readiness Routine",
            label_de: "Physical Readiness Routine",
            href: PDF_PATHS.physicalRoutine,
          },
        ],
        resources: [
          {
            label_en: "rv-fit free prevention course",
            label_de: "rv-fit Präventionskurs",
            href: "https://www.rv-fit.de/DE/home/home_node.html",
          },
          {
            label_en: "WHO physical activity guidelines",
            label_de: "WHO Bewegungsempfehlungen",
            href: "https://www.who.int/europe/publications/i/item/9789240014886",
          },
        ],
        safety_en:
          "Adapt exercise to your health status. Stop and seek advice for chest pain, dizziness, fainting or severe breathlessness.",
        safety_de:
          "Passe Übungen an deinen Gesundheitszustand an. Bei Brustschmerz, Schwindel, Ohnmacht oder starker Atemnot abbrechen und Hilfe suchen.",
      },
      2: {
        objective_en: "Climb stairs quickly, leave a fire scene safely and maintain basic fitness.",
        objective_de:
          "Treppen schneller steigen, Brandorte sicher verlassen und Grundfitness erhalten.",
        action_en: "Take stairs whenever possible, jog off-road, and follow the readiness routine.",
        action_de:
          "Nutze Treppen, jogge gelegentlich im Gelände und folge der Readiness-Routine.",
      },
      3: {
        objective_en: "Build the endurance required for sustained movement during disruptions or emergencies.",
        objective_de: "Die Ausdauer aufbauen, die für längere Fortbewegung während Störungen oder Notfällen erforderlich ist.",
        action_en: "Walk 5 km continuously at a comfortable pace without requiring extended breaks.",
        action_de: "5 km am Stück in einem angenehmen Tempo gehen, ohne längere Pausen zu benötigen."
      },
      4: {
        objective_en: "Develop the physical resilience needed to travel longer distances when transport options are unavailable.",
        objective_de: "Die körperliche Belastbarkeit entwickeln, die erforderlich ist, um längere Strecken zurückzulegen, wenn Transportmöglichkeiten ausfallen.",
        action_en: "Walk 10 km continuously while maintaining a steady pace and energy level.",
        action_de: "10 km am Stück mit gleichmäßigem Tempo und ausreichender Ausdauer zurücklegen."
      },
      5: {
        objective_en: "Carry a backpack, help others and move heavy objects.",
        objective_de: "Rucksack tragen, anderen helfen und schwere Gegenstände bewegen können.",
        action_en:
          "Practice push-ups, planks, squats and safe carrying techniques for people with limited mobility.",
        action_de:
          "Übe Push-ups, Planks, Squats und sichere Tragetechniken für Menschen mit eingeschränkter Mobilität.",
      },
      6: {
        objective_en: "Integrate exercises into your daily routine.",
        objective_de: "Übungen in den Alltag integrieren.",
        action_en: "Use walks, mobility, stairs, loaded-backpack walks, chair squats and wall push-ups.",
        action_de: "Nutze Spaziergänge, Mobility, Treppen, Rucksack-Gehen, Stuhl-Squats und Wand-Liegestütze.",
      },
      7: {
        objective_en: "Build upper-body strength for lifting, pushing and supporting others.",
        objective_de: "Oberkörperkraft für Heben, Schieben und Unterstützung anderer aufbauen.",
        action_en:
          "Practice push-ups regularly and gradually increase repetitions.",
        action_de:
          "Übe regelmäßig Liegestütze und steigere die Wiederholungen schrittweise.",
      },
      8: {
        objective_en: "Strengthen core stability and posture.",
        objective_de: "Rumpfstabilität und Körperhaltung stärken.",
        action_en:
          "Hold plank positions regularly and increase duration over time.",
        action_de:
          "Halte regelmäßig Plank-Positionen und verlängere die Dauer schrittweise.",
      },
      9: {
        objective_en: "Develop lower-body strength for lifting and movement.",
        objective_de: "Beinkraft für Heben und Fortbewegung entwickeln.",
        action_en:
          "Perform bodyweight squats regularly and focus on proper technique.",
        action_de:
          "Führe regelmäßig Kniebeugen mit sauberer Technik durch.",
      },
      10: {
        objective_en: "Prepare for lifting and transporting heavy items during emergencies.",
        objective_de: "Auf das Heben und Transportieren schwerer Gegenstände in Notfällen vorbereiten.",
        action_en:
          "Practice carrying moderately heavy objects safely while maintaining good posture.",
        action_de:
          "Übe das sichere Tragen mittelschwerer Gegenstände mit guter Körperhaltung.",
      },
      11: {
        objective_en: "Know how to assist and move another person safely when necessary.",
        objective_de: "Wissen, wie eine andere Person bei Bedarf sicher unterstützt und bewegt werden kann.",
        action_en:
          "Learn basic assisted-carry techniques appropriate for your abilities.",
        action_de:
          "Lerne grundlegende Tragetechniken für unterstützungsbedürftige Personen entsprechend deiner Fähigkeiten.",
      },
      12: {
        objective_en: "Prepare for evacuating with children while carrying essential equipment.",
        objective_de: "Auf eine Evakuierung mit Kindern und notwendiger Ausrüstung vorbereitet sein.",
        action_en:
          "Practice carrying a child together with an emergency backpack in a safe environment.",
        action_de:
          "Übe in sicherer Umgebung das Tragen eines Kindes zusammen mit einem Notfallrucksack.",
      },
      13: {
        objective_en: "Build practical carrying strength for everyday emergencies.",
        objective_de: "Praktische Tragkraft für alltägliche Notfälle aufbauen.",
        action_en:
          "Carry grocery bags by hand instead of using carts whenever it is safe and practical.",
        action_de:
          "Trage Einkaufstaschen wenn möglich selbst, anstatt Wagen oder Hilfsmittel zu verwenden.",
      },
      14: {
        objective_en: "Maintain mobility and walking endurance.",
        objective_de: "Mobilität und Geh-Ausdauer erhalten.",
        action_en:
          "Walk to stations, use stairs and spend more time moving without relying solely on a car.",
        action_de:
          "Gehe zu Haltestellen zu Fuß, nutze Treppen und bewege dich häufiger ohne ausschließlich auf das Auto angewiesen zu sein.",
      },
      15: {
        objective_en: "Increase strength and endurance during normal daily activities.",
        objective_de: "Kraft und Ausdauer während alltäglicher Aktivitäten steigern.",
        action_en:
          "Use light wrist or ankle weights during walks or simple daily activities if appropriate for your fitness level.",
        action_de:
          "Nutze leichte Hand- oder Fußgewichte bei Spaziergängen oder einfachen Alltagsaktivitäten, sofern dies zu deinem Fitnessniveau passt.",
      },
      16: {
        objective_en: "Improve flexibility and reduce injury risk.",
        objective_de: "Beweglichkeit verbessern und Verletzungsrisiken reduzieren.",
        action_en:
          "Perform simple stretching exercises several times per week.",
        action_de:
          "Führe mehrmals pro Woche einfache Dehnübungen durch.",
      },
      17: {
        objective_en: "Improve balance, posture and core stability.",
        objective_de: "Gleichgewicht, Haltung und Rumpfstabilität verbessern.",
        action_en:
          "Use an exercise ball for sitting, balance exercises or core training.",
        action_de:
          "Nutze einen Gymnastikball zum Sitzen, für Gleichgewichtsübungen oder Rumpftraining.",
      },
      18: {
        objective_en: "Build grip strength for carrying, lifting and emergency tasks.",
        objective_de: "Griffkraft für Tragen, Heben und Notfallaufgaben aufbauen.",
        action_en:
          "Train grip strength regularly using a hand gripper or similar tool.",
        action_de:
          "Trainiere regelmäßig deine Griffkraft mit einem Handtrainer oder einem ähnlichen Hilfsmittel.",
          resources: [
            {
              label_en: "Hand trainer / grip strengthener",
              label_de: "Handtrainer",
              href: "https://www.gorillasports.de/products/fingerhantel-schwarz-orange-mit-schaumstoff",
            },
        ],
      },
      19: {
        objective_en: "Reduce stiffness and maintain mobility during desk work.",
        objective_de: "Verspannungen reduzieren und Beweglichkeit bei Büroarbeit erhalten.",
        action_en: "Take short movement and stretching breaks throughout the workday.",
        action_de: "Mache während des Arbeitstags kurze Bewegungs- und Dehnpausen.",
      },
      20: {
        objective_en: "Improve mobility and flexibility with low-impact exercise.",
        objective_de: "Beweglichkeit und Flexibilität durch gelenkschonende Übungen verbessern.",
        action_en: "Practice simple chair-yoga exercises suitable for your abilities.",
        action_de: "Führe einfache Chair-Yoga-Übungen durch, die zu deinen Fähigkeiten passen.",
        resources: [
          {
            label_en: "Chair yoga video",
            label_de: "Chair-Yoga-Video",
            href: "https://www.youtube.com/watch?v=M8tjRkVnWqE",
          },
        ]
      },
    },
  },

  8: {
    outcome_en:
      "After this module, you can stay calm, make clearer decisions and manage stress under pressure.",
    outcome_de:
      "Nach diesem Modul kannst du ruhiger bleiben, klarere Entscheidungen treffen und Stress unter Druck besser bewältigen.",
    tasks: {
      1: {
        objective_en: "Recognize stress reactions before they become overwhelming.",
        objective_de: "Stressreaktionen erkennen, bevor sie überwältigend werden.",
        action_en: "Identify your personal physical warning signs such as tension, headaches, rapid breathing or sleep problems.",
        action_de: "Identifiziere persönliche Warnsignale wie Anspannung, Kopfschmerzen, schnelle Atmung oder Schlafprobleme.",
      },
      2: {
        objective_en: "Reduce stress through controlled breathing.",
        objective_de: "Stress durch kontrollierte Atmung reduzieren.",
        action_en: "Practice the three-minute breathing technique regularly so it becomes familiar during stressful situations.",
        action_de: "Übe die Drei-Minuten-Atemtechnik regelmäßig, damit sie in Stresssituationen vertraut ist.",
        resources: [
          {
            label_en: "3-Minute Breathing Technique video",
            label_de: "3-Minuten-Atemtechnik Video",
            href: "https://www.youtube.com/watch?v=wPoj5log_7M",
          },
        ],

      },
      3: {
        objective_en: "Understand how you currently react to stress.",
        objective_de: "Verstehen, wie du aktuell auf Stress reagierst.",
        action_en:
          "Reflect on which coping strategies help you and which ones may create additional stress.",
        action_de:
          "Reflektiere, welche Bewältigungsstrategien hilfreich sind und welche zusätzlichen Stress erzeugen.",
      },
      4: {
        objective_en: "Reduce mental overload by externalizing worries.",
        objective_de: "Mentale Überlastung reduzieren, indem Sorgen aufgeschrieben werden.",
        action_en:
          "Write down recurring crisis-related thoughts instead of repeatedly reviewing them mentally.",
        action_de:
          "Schreibe wiederkehrende krisenbezogene Gedanken auf, anstatt sie ständig im Kopf zu wiederholen.",
      },
      5: {
        objective_en: "Interrupt repetitive negative thought loops.",
        objective_de: "Wiederkehrende negative Gedankenspiralen unterbrechen.",
        action_en:
          "Practice consciously stopping unhelpful thought loops and redirecting attention toward actionable steps.",
        action_de:
          "Übe das bewusste Unterbrechen negativer Gedankenspiralen und richte die Aufmerksamkeit auf konkrete Handlungsschritte.",
      },
      6: {
        objective_en: "Focus energy on things you can influence.",
        objective_de: "Energie auf beeinflussbare Dinge konzentrieren.",
        action_en:
          "List what is within your control and separate it from factors outside your influence.",
        action_de:
          "Liste auf, was du kontrollieren kannst, und trenne es von Dingen außerhalb deines Einflusses.",
      },
      7: {
        objective_en: "Create momentum through achievable actions.",
        objective_de: "Durch erreichbare Schritte positive Dynamik erzeugen.",
        action_en:
          "Choose one small practical action each week and complete it.",
        action_de:
          "Wähle jede Woche eine kleine praktische Maßnahme und setze sie um.",
      },
      8: {
        objective_en: "Create a mental refuge during stressful situations.",
        objective_de: "Einen inneren sicheren Ort für belastende Situationen schaffen.",
        action_en:
          "Practice visualizing a place where you feel calm, safe and protected.",
        action_de:
          "Übe die Vorstellung eines Ortes, an dem du dich ruhig, sicher und geschützt fühlst.",
      },
      9: {
        objective_en: "Recognize strengths you can rely on during crises.",
        objective_de: "Eigene Stärken erkennen, auf die du in Krisen zurückgreifen kannst.",

        action_en:
          "Create a list of skills, experiences, people and resources that support you.",
        action_de:
          "Erstelle eine Liste von Fähigkeiten, Erfahrungen, Menschen und Ressourcen, die dich unterstützen.",
      },
      10: {
        objective_en: "Reduce sensory overload and regain focus.",
        objective_de: "Reizüberflutung reduzieren und Fokus zurückgewinnen.",

        action_en:
          "Develop a short routine that helps you calm down when overwhelmed.",
        action_de:
          "Entwickle eine kurze Routine, die dir hilft, dich bei Überforderung zu beruhigen.",
      },
      11: {
        objective_en: "Have clear support steps available during emotional distress.",
        objective_de: "Klare Unterstützungsschritte bei emotionaler Belastung verfügbar haben.",

        action_en:
          "Complete the Mental Resilience Emergency Card and keep it accessible.",
        action_de:
          "Fülle die Mental Resilience Emergency Card aus und halte sie griffbereit.",

        downloads: [
          {
            label_en: "Mental Resilience Emergency Card",
            label_de: "Mental Resilience Emergency Card",
            href: PDF_PATHS.mentalCard,
          },
        ],

        resources: [
          {
            label_en: "WHO Stress Guide",
            label_de: "WHO Stress-Leitfaden",
            href: "https://www.who.int/publications/i/item/9789240003927",
          },
        ],
      },
      12: {
        objective_en: "Recognize situations that commonly create stress.",
        objective_de: "Situationen erkennen, die häufig Stress auslösen.",

        action_en:
          "List situations, topics or events that typically trigger stress reactions.",
        action_de:
          "Liste Situationen, Themen oder Ereignisse auf, die typischerweise Stress auslösen.",
      },
      13: {
        objective_en: "Reduce resistance to unavoidable situations.",
        objective_de: "Widerstand gegen unvermeidbare Situationen reduzieren.",

        action_en:
          "Practice acknowledging difficult situations while focusing on your next useful action.",
        action_de:
          "Übe, schwierige Situationen anzuerkennen und dich auf den nächsten hilfreichen Schritt zu konzentrieren.",
      },
      14: {
        objective_en: "Create psychological stability during uncertainty.",
        objective_de: "Psychologische Stabilität in unsicheren Situationen schaffen.",

        action_en:
          "Identify people, values or routines that help you feel grounded.",
        action_de:
          "Identifiziere Menschen, Werte oder Routinen, die dir Stabilität geben.",
      },
      15: {
        objective_en: "Know what to do when emotions become overwhelming.",
        objective_de: "Wissen, was zu tun ist, wenn Emotionen überwältigend werden.",

        action_en:
          "Write down simple steps to follow when you feel overwhelmed or unable to think clearly.",
        action_de:
          "Schreibe einfache Schritte auf, die du befolgen kannst, wenn du dich überfordert fühlst.",
      },
      16: {
        objective_en: "Build long-term psychological resilience.",
        objective_de: "Langfristige psychische Resilienz aufbauen.",

        action_en:
          "Create regular habits that support emotional stability and recovery.",
        action_de:
          "Etabliere regelmäßige Gewohnheiten zur emotionalen Stabilität und Erholung.",
      },
      17: {
        objective_en: "Strengthen social support networks.",
        objective_de: "Soziale Unterstützungsnetzwerke stärken.",

        action_en:
          "Maintain regular contact with friends, family or trusted colleagues.",
        action_de:
          "Pflege regelmäßigen Kontakt zu Freunden, Familie oder vertrauten Kolleginnen und Kollegen.",
      },
      18: {
        objective_en: "Prepare mentally for possible crisis situations.",
        objective_de: "Sich mental auf mögliche Krisensituationen vorbereiten.",

        action_en:
          "List realistic scenarios that could affect your household or daily life.",
        action_de:
          "Liste realistische Szenarien auf, die deinen Haushalt oder Alltag betreffen könnten.",
      },
      19: {
        objective_en: "Build confidence through mental rehearsal.",
        objective_de: "Selbstvertrauen durch mentale Vorbereitung aufbauen.",

        action_en:
          "Mentally walk through a crisis scenario and visualize how you would respond step by step.",
        action_de:
          "Gehe ein Krisenszenario gedanklich durch und stelle dir vor, wie du Schritt für Schritt reagieren würdest.",
      },
    },
  },

  9: {
    header_en:
      "Support others responsibly by starting with yourself, then expanding outward: Yourself → Family → Neighbors → Community → Official organizations.",
    header_de:
      "Unterstütze andere verantwortungsvoll, indem du bei dir selbst beginnst und dann nach außen erweiterst: Du selbst → Familie → Nachbarn → Community → offizielle Organisationen.",
    outcome_en:
      "After this module, you can help your community without burning out — starting with yourself, then expanding outward.",
    outcome_de:
      "Nach diesem Modul kannst du deiner Gemeinschaft helfen, ohne auszubrennen — beginnend bei dir selbst und dann nach außen.",
    tasks: {
      1: {
        objective_en: "Find opportunities to volunteer.",
        objective_de: "Möglichkeiten für freiwilliges Engagement finden.",
        action_en:
          "Research volunteer opportunities in your area, such as THW, DRK or volunteer fire departments.",
        action_de:
          "Recherchiere Engagement-Möglichkeiten in deiner Region, z. B. THW, DRK oder Freiwillige Feuerwehr.",
        resources: [
          {
            label_en: "BBK spontaneous helpers",
            label_de: "BBK Spontanhelfende",
            href: "https://www.bbk.bund.de/EN/Home/home_node.html",
          },
        ],
      },
      2: {
        objective_en: "Help others become prepared.",
        objective_de: "Anderen helfen, besser vorbereitet zu sein.",
        action_en: "Share your checklists and what you have learned with family, friends and neighbors.",
        action_de:
          "Teile deine Checklisten und Erkenntnisse mit Familie, Freund:innen und Nachbar:innen.",
      },
      3: {
        objective_en: "Know who to contact before, during and after a crisis.",
        objective_de: "Wissen, wen du vor, während und nach einer Krise kontaktieren kannst.",
        action_en: "Create a list of relevant local contacts, support organizations and trusted individuals who may be helpful during emergencies.",
        action_de: "Erstelle eine Liste relevanter lokaler Ansprechpartner, Hilfsorganisationen und Vertrauenspersonen, die in Notfällen unterstützen können.",
        note_en: "Include local authorities, municipal services, healthcare providers, community groups and trusted neighbors where appropriate.",
        note_de: "Berücksichtige lokale Behörden, kommunale Dienste, Gesundheitsangebote, Gemeinschaftsgruppen und vertrauenswürdige Nachbarn.",
        resources: [
          {
            label_en: "Municipal emergency information",
            label_de: "Kommunale Notfallinformationen",
            href: "https://www.berlin.de/sen/gesundheit/gesundheitswesen/notdienste/",
          },
        ],
      },
      4: {
        objective_en: "Provide orientation and radiate calm.",
        objective_de: "Orientierung geben und Ruhe ausstrahlen.",
        action_en:
          "Find out who in your neighborhood is well prepared and who might need support in an emergency.",
        action_de:
          "Finde heraus, wer in deiner Nachbarschaft gut vorbereitet ist und wer im Notfall Unterstützung brauchen könnte.",
      },
      5: {
        objective_en: "Understand who may require additional support during a crisis.",
        objective_de: "Verstehen, wer in einer Krise zusätzliche Unterstützung benötigen könnte.",
        action_en: "Identify vulnerable individuals in your immediate environment, such as elderly people, persons with disabilities or households with young children.",
        action_de: "Identifiziere besonders unterstützungsbedürftige Personen in deinem Umfeld, beispielsweise ältere Menschen, Menschen mit Behinderungen oder Haushalte mit kleinen Kindern.",
        note_en: "Focus on awareness and preparedness rather than responsibility. Support should always remain voluntary and within your capabilities.",
        note_de: "Der Fokus liegt auf Aufmerksamkeit und Vorbereitung, nicht auf Verantwortung. Unterstützung sollte freiwillig und im Rahmen deiner Möglichkeiten erfolgen.",
      },
      6: {
        objective_en: "Build collective resilience.",
        objective_de: "Gemeinsame Resilienz aufbauen.",
        action_en:
          "Identify options for jointly used emergency supplies, such as shared storage boxes.",
        action_de:
          "Prüfe Möglichkeiten für gemeinsam genutzte Notfallvorräte, z. B. geteilte Lagerboxen.",
          resources: [
          {
            label_en: "Tamburi - shared storage solutions",
            label_de: "Tamburi - Shared-Storage-Lösungen",
            href: "https://tamburi.com/at/home/",
          },
        ],
      },
      7: {
        objective_en: "Explore opportunities to contribute to community resilience.",
        objective_de: "Möglichkeiten kennenlernen, zur Resilienz der Gemeinschaft beizutragen.",
        action_en: "Research local volunteer organizations and consider whether joining a civil protection, emergency response or humanitarian organization is right for you.",
        action_de: "Informiere dich über lokale Ehrenamtsmöglichkeiten und prüfe, ob eine Mitarbeit im Bevölkerungsschutz, Rettungswesen oder einer Hilfsorganisation für dich geeignet ist.",
        resources: [
          {
            label_en: "THW",
            label_de: "THW",
            href: "https://www.thw.de",
          },
          {
            label_en: "German Red Cross",
            label_de: "Deutsches Rotes Kreuz",
            href: "https://www.drk.de",
          },
        ],
      },
      8: {
        objective_en: "Ensure others know how to reach you.",
        objective_de: "Sicherstellen, dass wichtige Personen wissen, wie sie dich erreichen.",
        action_en: "Share your Family Communication Plan with key contacts.",
        action_de: "Teile deinen Familien-Kommunikationsplan mit wichtigen Kontakten.",
        downloads: [
          {
            label_en: "Family Communication Plan",
            label_de: "Familien-Kommunikationsplan",
            href: PDF_PATHS.familyComms,
          },
        ],
      },
    },
  },
};

function getText(en: string | null | undefined, de: string | null | undefined, lang: "en" | "de") {
  return lang === "de" ? de || en || "" : en || de || "";
}

function isHttpUrl(value: string) {
  return /^https?:\/\//i.test(value);
}

async function resolveVideoUrl(videoUrl: string | null | undefined) {
  if (!videoUrl) return null;
  if (isHttpUrl(videoUrl)) return videoUrl;
  return getSignedVideoUrl("actionplan", videoUrl);
}

function getEnrichment(moduleOrder: number, taskIndex: number) {
  return MODULE_ENRICHMENT[moduleOrder]?.tasks?.[taskIndex + 1] ?? null;
}

export default function ModuleWrapper(props: ModuleWrapperProps) {
  const { handlePageChange, moduleOrder } = props;

  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";
  const L = React.useCallback(
    (en: string, de: string) => (typedLang === "de" ? de : en),
    [typedLang]
  );

  const [employeeId, setEmployeeId] = React.useState<string | null>(null);
  const [module, setModule] = React.useState<Module | null>(null);
  const [progress, setProgress] = React.useState<Record<string, boolean>>({});
  const [videoSrc, setVideoSrc] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [savingTaskId, setSavingTaskId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [completionDialogOpen, setCompletionDialogOpen] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const empId = await getMyEmployeeId();
        const [moduleRow, progressMap] = await Promise.all([
          fetchModuleByOrder(moduleOrder),
          fetchMyModuleTaskProgress(empId),
        ]);

        const resolvedVideo = await resolveVideoUrl(moduleRow.video_url);

        if (!mounted) return;

        setEmployeeId(empId);
        setModule(moduleRow);
        setProgress(progressMap);
        setVideoSrc(resolvedVideo);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? L("Failed to load module.", "Modul konnte nicht geladen werden."));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [moduleOrder, L]);

  async function toggleTask(task: ModuleTask, checked: boolean) {
    if (!employeeId) return;

    const previous = Boolean(progress[task.id]);
    const willCompleteModule =
      checked &&
      !previous &&
      allTasks.length > 0 &&
      allTasks.every((item) => item.id === task.id || Boolean(progress[item.id]));

    setSavingTaskId(task.id);
    setProgress((old) => {
      const next = { ...old };
      if (checked) next[task.id] = true;
      else delete next[task.id];
      return next;
    });
    setError(null);

    try {
      await setModuleTaskDone({
        employeeId,
        moduleTaskId: task.id,
        isDone: checked,
      });
      if (willCompleteModule) {
        setCompletionDialogOpen(true);
      }
    } catch (e: any) {
      setProgress((old) => {
        const next = { ...old };
        if (previous) next[task.id] = true;
        else delete next[task.id];
        return next;
      });
      setError(e?.message ?? L("Failed to save progress.", "Fortschritt konnte nicht gespeichert werden."));
    } finally {
      setSavingTaskId(null);
    }
  }

  const categories = React.useMemo(() => {
    return (module?.module_task_categories ?? [])
      .slice()
      .sort((a, b) => Number(a.order_index ?? 0) - Number(b.order_index ?? 0));
  }, [module]);

  const categoriesWithGlobalTaskIndex = React.useMemo(() => {
    let globalTaskIndex = 0;

    return categories.map((category) => {
      const tasks = (category.module_tasks ?? []).map((task) => {
        const indexedTask = {
          task,
          globalTaskIndex,
        };

        globalTaskIndex += 1;

        return indexedTask;
      });

      return {
        category,
        tasks,
      };
    });
  }, [categories]);

  const allTasks = categoriesWithGlobalTaskIndex.flatMap((entry) =>
    entry.tasks.map((item) => item.task)
  );
  const doneCount = allTasks.filter((task) => progress[task.id]).length;
  const total = allTasks.length;
  const status =
    total === 0
      ? "not_started"
      : doneCount === 0
      ? "not_started"
      : doneCount === total
      ? "completed"
      : "in_progress";

  const title = module ? getText(module.name_en, module.name_de, typedLang) : L("Module", "Modul");
  const description = module ? getText(module.description_en, module.description_de, typedLang) : "";
  const moduleEnrichment = MODULE_ENRICHMENT[moduleOrder] ?? null;
  const outcome = moduleEnrichment
    ? getText(moduleEnrichment.outcome_en, moduleEnrichment.outcome_de, typedLang)
    : "";
  const headerText = moduleEnrichment
    ? getText(moduleEnrichment.header_en, moduleEnrichment.header_de, typedLang)
    : "";
  const moduleNumber = Number(module?.order_index ?? moduleOrder);
  const moduleLabel = L(`Module ${moduleNumber}`, `Modul ${moduleNumber}`);
  const completionExplanation =
    outcome ||
    L(
      "You completed the checklist for this module. You are now better prepared to apply these steps in a real situation and keep your readiness stronger.",
      "Du hast die Checkliste für dieses Modul abgeschlossen. Du bist jetzt besser vorbereitet, diese Schritte in einer realen Situation anzuwenden und deine Bereitschaft zu stärken."
    );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => handlePageChange("ActionPlan")}
            sx={btnOutlineSx}
          >
            {L("Back to overview", "Zurück zur Übersicht")}
          </Button>

          {!loading && module?.order_index ? (
            <Chip
              label={`#${module.order_index}`}
              sx={{ bgcolor: "#111827", color: "#FFFFFF", fontWeight: 900 }}
            />
          ) : null}
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {error}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
            mb: 2,
          }}
        >
          <Box sx={{ p: 2.25, bgcolor: "#EEF2FF", borderBottom: "1px solid #E5E7EB" }}>
            {loading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={34} width="50%" />
                <Skeleton variant="rounded" height={18} width="80%" />
              </Stack>
            ) : (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <ChecklistOutlinedIcon fontSize="small" />
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827" }}>
                    {title}
                  </Typography>
                </Stack>

                {description ? (
                  <Typography variant="body2" sx={{ color: "#6B7280", maxWidth: 850, lineHeight: 1.65 }}>
                    {description}
                  </Typography>
                ) : null}

                {headerText ? (
                  <Typography variant="body2" sx={{ color: "#374151", maxWidth: 850, lineHeight: 1.65, mt: 1 }}>
                    {headerText}
                  </Typography>
                ) : null}

                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                  {status === "not_started" ? (
                    <Chip
                      size="small"
                      label={L("Not started", "Noch nicht begonnen")}
                      sx={statusChipSx.notStarted}
                    />
                  ) : status === "completed" ? (
                    <Chip
                      size="small"
                      label={L("Completed", "Abgeschlossen")}
                      sx={statusChipSx.completed}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label={`${doneCount}/${total} ${L("done", "erledigt")}`}
                      sx={statusChipSx.inProgress}
                    />
                  )}
                </Stack>
              </>
            )}
          </Box>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", overflow: "hidden" }}>
              <Box sx={{ p: 2, borderBottom: "1px solid #E5E7EB" }}>
                <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                  {L("Module video", "Modulvideo")}
                </Typography>
              </Box>

              {loading ? (
                <Skeleton variant="rectangular" height={320} />
              ) : videoSrc ? (
                <Box sx={{ bgcolor: "#111827" }}>
                  <Box
                    component="video"
                    src={videoSrc}
                    controls
                    preload="metadata"
                    playsInline
                    sx={{ width: "100%", height: "auto", display: "block" }}
                  />
                </Box>
              ) : (
                <Box sx={{ aspectRatio: "16 / 9", display: "grid", placeItems: "center", bgcolor: "#111827", color: "#FFFFFF", p: 2 }}>
                  <Typography sx={{ fontWeight: 900 }}>
                    {L("No video configured for this module yet.", "Für dieses Modul ist noch kein Video hinterlegt.")}
                  </Typography>
                </Box>
              )}
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", overflow: "hidden" }}>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <LightbulbOutlinedIcon fontSize="small" sx={{ color: brand.accentStrong, mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                      {outcome ? L("Outcome", "Ergebnis") : L("Quick tip", "Kurzer Tipp")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.75, lineHeight: 1.6 }}>
                      {outcome ||
                        L(
                          "Start small. Complete one task today, then come back and continue the checklist step by step.",
                          "Fang klein an. Erledige heute eine Aufgabe und setze die Checkliste Schritt für Schritt fort."
                        )}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #E5E7EB",
              bgcolor: "#FFFFFF",
              overflow: "hidden",
              maxHeight: { lg: "calc(100vh - 175px)" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #E5E7EB" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ChecklistOutlinedIcon fontSize="small" />
                <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                  {L("Action checklist", "Aktions-Checkliste")}
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 2, overflowY: "auto" }}>
              {loading ? (
                <Stack spacing={1.5}>
                  <Skeleton variant="rounded" height={160} />
                  <Skeleton variant="rounded" height={160} />
                  <Skeleton variant="rounded" height={160} />
                </Stack>
              ) : categories.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                  {L(
                    "No checklist categories are configured for this module yet.",
                    "Für dieses Modul sind noch keine Checklisten-Kategorien konfiguriert."
                  )}
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {categoriesWithGlobalTaskIndex.map(({ category, tasks }) => (
                    <Box key={category.id} sx={{ borderRadius: 2.5, border: "1px solid #E5E7EB", overflow: "hidden" }}>
                      <Box sx={{ p: 1.5, bgcolor: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                        <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                          {getText(category.title_en, category.title_de, typedLang)}
                        </Typography>
                        {getText(category.description_en, category.description_de, typedLang) ? (
                          <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.4, lineHeight: 1.55 }}>
                            {getText(category.description_en, category.description_de, typedLang)}
                          </Typography>
                        ) : null}
                      </Box>

                      <Stack divider={<Divider />}>
                        {tasks.length === 0 ? (
                          <Box sx={{ p: 1.5 }}>
                            <Typography variant="body2" sx={{ color: "#6B7280" }}>
                              {L("No tasks in this category yet.", "Noch keine Aufgaben in dieser Kategorie.")}
                            </Typography>
                          </Box>
                        ) : (
                          tasks.map(({ task, globalTaskIndex }) => (
                            <TaskRow
                              key={task.id}
                              task={task}
                              enrichment={getEnrichment(moduleOrder, globalTaskIndex)}
                              lang={typedLang}
                              checked={Boolean(progress[task.id])}
                              disabled={savingTaskId === task.id}
                              onToggle={(checked) => toggleTask(task, checked)}
                            />
                          ))
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={completionDialogOpen}
        onClose={() => setCompletionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "1px solid #BBF7D0",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "#16A34A",
                color: "#FFFFFF",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <CheckCircleOutlineIcon />
            </Box>
            <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 20 }}>
              {L(
                `Congratulations, you completed ${moduleLabel}`,
                `Glückwunsch, du hast ${moduleLabel} abgeschlossen`
              )}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ color: "#374151", lineHeight: 1.65 }}>
            {completionExplanation}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setCompletionDialogOpen(false)}
            sx={btnOutlineSx}
          >
            {L("Keep reviewing", "Weiter ansehen")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handlePageChange("ActionPlan")}
            sx={{
              ...btnPrimarySx,
              bgcolor: "#16A34A",
              "&:hover": { bgcolor: "#15803D", boxShadow: "none" },
            }}
          >
            {L("Back to overview", "Zur Übersicht")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function TaskRow(props: {
  task: ModuleTask;
  enrichment: EnrichedTaskContent | null;
  lang: "en" | "de";
  checked: boolean;
  disabled: boolean;
  onToggle: (checked: boolean) => void;
}) {
  const { task, enrichment, lang, checked, disabled, onToggle } = props;

  const label = getText(task.task_en, task.task_de, lang);
  const objective = enrichment
    ? getText(enrichment.objective_en, enrichment.objective_de, lang)
    : "";
  const action = enrichment ? getText(enrichment.action_en, enrichment.action_de, lang) : "";
  const note = enrichment ? getText(enrichment.note_en, enrichment.note_de, lang) : "";
  const safety = enrichment ? getText(enrichment.safety_en, enrichment.safety_de, lang) : "";

  const downloads = enrichment?.downloads ?? [];
  const resources = enrichment?.resources ?? [];

  return (
    <Box sx={{ px: 1.25, py: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
          sx={{ mt: -0.75, color: "#9CA3AF", "&.Mui-checked": { color: "#111827" } }}
        />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: checked ? "#6B7280" : "#111827",
              fontWeight: checked ? 700 : 800,
              lineHeight: 1.45,
              textDecoration: checked ? "line-through" : "none",
            }}
          >
            {label}
          </Typography>

          {objective ? (
            <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.75, lineHeight: 1.55 }}>
              <Box component="span" sx={{ fontWeight: 900, color: "#374151" }}>
                {lang === "de" ? "Ziel:" : "Objective:"}
              </Box>{" "}
              {objective}
            </Typography>
          ) : null}

          {action ? (
            <Typography variant="body2" sx={{ color: "#374151", mt: 0.5, lineHeight: 1.55 }}>
              <Box component="span" sx={{ fontWeight: 900 }}>
                {lang === "de" ? "Aktion:" : "Action:"}
              </Box>{" "}
              {action}
            </Typography>
          ) : null}

          {note ? (
            <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5, lineHeight: 1.55 }}>
              {note}
            </Typography>
          ) : null}

          {safety ? (
            <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
              {safety}
            </Alert>
          ) : null}

          {downloads.length > 0 || resources.length > 0 ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
              {downloads.map((item, idx) => (
                <ResourceButton key={`download-${idx}`} item={item} lang={lang} />
              ))}

              {resources.map((item, idx) => (
                <ResourceButton key={`resource-${idx}`} item={item} lang={lang} />
              ))}
            </Stack>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

function ResourceButton(props: { item: EnrichedLink; lang: "en" | "de" }) {
  const { item, lang } = props;
  const label = lang === "de" ? item.label_de || item.label_en : item.label_en || item.label_de;

  if (item.disabled || !item.href) {
    return (
      <Button size="small" variant="outlined" disabled sx={btnOutlineSmallSx}>
        {label}
      </Button>
    );
  }

  return (
    <Button
      size="small"
      variant="outlined"
      href={item.href}
      target="_blank"
      rel="noreferrer"
      endIcon={<OpenInNewIcon fontSize="small" />}
      sx={btnOutlineSmallSx}
    >
      {label}
    </Button>
  );
}

const statusChipSx = {
  notStarted: {
    fontWeight: 900,
    bgcolor: "#374151",
    border: "1px solid #374151",
    color: "#FFFFFF",
  },
  inProgress: {
    fontWeight: 900,
    bgcolor: "#F59E0B",
    border: "1px solid #F59E0B",
    color: "#111827",
  },
  completed: {
    fontWeight: 900,
    bgcolor: "#16A34A",
    border: "1px solid #16A34A",
    color: "#FFFFFF",
  },
} as const;

const btnOutlineSx = {
  textTransform: "none",
  borderRadius: 2.5,
  borderColor: "#D1D5DB",
  color: "#111827",
  bgcolor: "#FFFFFF",
  fontWeight: 900,
  "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
} as const;

const btnPrimarySx = {
  textTransform: "none",
  borderRadius: 2.5,
  bgcolor: "#111827",
  color: "#FFFFFF",
  fontWeight: 900,
  boxShadow: "none",
  "&:hover": { bgcolor: "#0B1220", boxShadow: "none" },
} as const;

const btnOutlineSmallSx = {
  textTransform: "none",
  borderRadius: 2,
  borderColor: "#D1D5DB",
  color: "#111827",
  bgcolor: "#FFFFFF",
  fontWeight: 900,
  px: 1.25,
  py: 0.55,
  "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
  "&.Mui-disabled": {
    borderColor: "#E5E7EB",
    bgcolor: "#F9FAFB",
    color: "#9CA3AF",
  },
} as const;